import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from './apiCache';
import { ENDPOINTS } from '../api/endpoints';

export default function Home() {
  const [stats, setStats] = useState({
    contactsCount: 0,
    booksCount: 0,
    videosCount: 0,
    noticesCount: 0
  });
  const [notices, setNotices] = useState([]);
  const [books, setBooks] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // High-speed aggregated stats endpoint (< 2KB response, ~100ms)
        const statsData = await apiGet(ENDPOINTS.DASHBOARD_STATS).catch(() => null);

        if (statsData && statsData.counts) {
          const { counts, recentNotices, recentBooks, recentCards } = statsData;
          setStats({
            contactsCount: counts.contacts || counts.staff || 0,
            booksCount: counts.books || 0,
            videosCount: counts.videos || 0,
            noticesCount: counts.notices || 0
          });
          setNotices(recentNotices || []);
          setBooks(recentBooks || []);
          setGallery(recentCards || []);
        } else {
          // Graceful fallback to individual endpoints
          const [nList, bList, vList, gList] = await Promise.all([
            apiGet(ENDPOINTS.NOTICES.GET_ALL).catch(() => []),
            apiGet(ENDPOINTS.BOOKS.GET_ALL).catch(() => []),
            apiGet(ENDPOINTS.VIDEOS.GET_ALL).catch(() => []),
            apiGet(ENDPOINTS.CARDS.GET_ALL).catch(() => [])
          ]);

          const noticesArray = Array.isArray(nList) ? nList : [];
          const booksArray = Array.isArray(bList) ? bList : [];
          const videosArray = Array.isArray(vList) ? vList : [];
          const galleryArray = Array.isArray(gList) ? gList : [];

          setStats({
            contactsCount: 0,
            booksCount: booksArray.length,
            videosCount: videosArray.length,
            noticesCount: noticesArray.length
          });
          setNotices(noticesArray.slice(-3).reverse());
          setBooks(booksArray.slice(-3).reverse());
          setGallery(galleryArray.slice(-2));
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div className="page-container">
      {/* Welcome Banner */}
      <div className="content-card mb-4 text-white position-relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #005BAC 0%, #0A74D8 100%)',
        border: 'none',
        borderRadius: 'var(--radius-lg)'
      }}>
        <div className="position-relative z-3">
          <span className="badge mb-2 text-uppercase tracking-wider font-semibold" style={{ background: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }}>
            Official Internal Portal
          </span>
          <h1 className="fw-bold mb-2" style={{ fontSize: '28px' }}>Crew Booking Center (CCC)</h1>
          <p className="lead mb-0 text-white-50" style={{ fontSize: '15px' }}>Bilaspur Division (SECR) - SECR Crew Management Information Systems</p>
        </div>
      </div>

      {/* Real Statistics Widget Cards */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="content-card text-center p-3">
            <div className="d-flex align-items-center justify-content-between">
              <span className="text-muted font-semibold text-sm">Active Crew</span>
              <i className="bi bi-people-fill text-primary fs-5"></i>
            </div>
            <h3 className="fw-bold text-main mt-2 mb-0 text-start">{isLoading ? '...' : (stats.contactsCount || '4,400+')}</h3>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="content-card text-center p-3">
            <div className="d-flex align-items-center justify-content-between">
              <span className="text-muted font-semibold text-sm">Books & Rules</span>
              <i className="bi bi-book-half text-success fs-5"></i>
            </div>
            <h3 className="fw-bold text-main mt-2 mb-0 text-start">{isLoading ? '...' : stats.booksCount}</h3>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="content-card text-center p-3">
            <div className="d-flex align-items-center justify-content-between">
              <span className="text-muted font-semibold text-sm">Safety Videos</span>
              <i className="bi bi-play-btn-fill text-warning fs-5"></i>
            </div>
            <h3 className="fw-bold text-main mt-2 mb-0 text-start">{isLoading ? '...' : stats.videosCount}</h3>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="content-card text-center p-3">
            <div className="d-flex align-items-center justify-content-between">
              <span className="text-muted font-semibold text-sm">Division Notices</span>
              <i className="bi bi-megaphone-fill text-danger fs-5"></i>
            </div>
            <h3 className="fw-bold text-main mt-2 mb-0 text-start">{isLoading ? '...' : stats.noticesCount}</h3>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Left Side: Real Notices and Content Feed */}
        <div className="col-lg-8">
          
          {/* Latest Active Notices Feed */}
          <div className="content-card mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0 text-main"><i className="bi bi-bell-fill text-primary me-2"></i>Latest Notices</h5>
              <Link to="/Notice" className="btn btn-sm btn-outline-custom py-1">View Notices</Link>
            </div>
            
            {isLoading ? (
              <div className="skeleton-row"></div>
            ) : notices.length === 0 ? (
              <div className="text-center py-4 bg-light rounded" style={{ backgroundColor: 'var(--bg-main)' }}>
                <p className="text-muted m-0">No announcements posted recently.</p>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {(notices.length > 3 ? notices.slice(-3).reverse() : notices).map((notice, idx) => (
                  <div key={idx} className="p-3 border rounded-3 bg-light" style={{ backgroundColor: 'var(--bg-main)' }}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="fw-bold text-main mb-0">{notice.Title}</h6>
                      <small className="text-muted">{notice.TimeAdded}</small>
                    </div>
                    <p className="mb-0 text-muted text-sm" style={{ whiteSpace: 'pre-wrap' }}>{notice.Description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Operations Links */}
          <div className="content-card mb-4">
            <h5 className="fw-bold text-main mb-3"><i className="bi bi-grid-fill text-primary me-2"></i>Quick Operations Navigation</h5>
            <div className="row g-3">
              <div className="col-6">
                <Link to="/Contact" className="btn btn-light w-100 text-start p-3 border d-flex align-items-center gap-3" style={{ backgroundColor: 'var(--bg-main)', borderRadius: 'var(--radius-sm)' }}>
                  <i className="bi bi-people-fill text-primary fs-4"></i>
                  <div>
                    <div className="fw-bold text-main text-sm">Staff Directory</div>
                    <small className="text-muted text-xs">Search running staff database</small>
                  </div>
                </Link>
              </div>
              <div className="col-6">
                <Link to="/Books" className="btn btn-light w-100 text-start p-3 border d-flex align-items-center gap-3" style={{ backgroundColor: 'var(--bg-main)', borderRadius: 'var(--radius-sm)' }}>
                  <i className="bi bi-file-earmark-text-fill text-success fs-4"></i>
                  <div>
                    <div className="fw-bold text-main text-sm">Books & Circulars</div>
                    <small className="text-muted text-xs">View rules and directives</small>
                  </div>
                </Link>
              </div>
              <div className="col-6">
                <Link to="/Videos" className="btn btn-light w-100 text-start p-3 border d-flex align-items-center gap-3" style={{ backgroundColor: 'var(--bg-main)', borderRadius: 'var(--radius-sm)' }}>
                  <i className="bi bi-play-btn-fill text-warning fs-4"></i>
                  <div>
                    <div className="fw-bold text-main text-sm">Safety Videos</div>
                    <small className="text-muted text-xs">Troubleshooting locomotives</small>
                  </div>
                </Link>
              </div>
              <div className="col-6">
                <Link to="/Quiz" className="btn btn-light w-100 text-start p-3 border d-flex align-items-center gap-3" style={{ backgroundColor: 'var(--bg-main)', borderRadius: 'var(--radius-sm)' }}>
                  <i className="bi bi-patch-check-fill text-danger fs-4"></i>
                  <div>
                    <div className="fw-bold text-main text-sm">Practice Quiz</div>
                    <small className="text-muted text-xs">Test rules and operations</small>
                  </div>
                </Link>
              </div>
            </div>
          </div>

        </div>

        {/* Right Side: Circulars & Gallery Highlights */}
        <div className="col-lg-4">

          {/* Recently Added Documents */}
          <div className="content-card mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold text-main mb-0"><i className="bi bi-file-earmark-pdf-fill text-danger me-2"></i>Recent Documents</h5>
              <Link to="/Books" className="btn btn-sm btn-link text-decoration-none">View All</Link>
            </div>
            
            {isLoading ? (
              <div className="skeleton-row"></div>
            ) : books.length === 0 ? (
              <p className="text-muted text-center py-2 m-0">No documents found.</p>
            ) : (
              <div className="d-flex flex-column gap-3">
                {(books.length > 3 ? books.slice(-3).reverse() : books).map((book, idx) => (
                  <div key={idx} className="p-2 border rounded bg-light" style={{ backgroundColor: 'var(--bg-main)' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="text-truncate" style={{ maxWidth: '75%' }}>
                        <h6 className="fw-bold text-main text-sm text-truncate mb-0">{book.Name}</h6>
                        <small className="text-muted text-xs">{book.Category || 'Circular'}</small>
                      </div>
                      <a href={book.Link} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary py-1 px-2">
                        Open
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Gallery Highlights (Real Cards) */}
          <div className="content-card">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold text-main mb-0"><i className="bi bi-images text-primary me-2"></i>Gallery Highlights</h5>
              <Link to="/Gallery" className="btn btn-sm btn-link text-decoration-none">All Images</Link>
            </div>

            {isLoading ? (
              <div className="skeleton-card" style={{ height: '140px' }}></div>
            ) : gallery.length === 0 ? (
              <p className="text-muted text-center py-2 m-0">No gallery images.</p>
            ) : (
              <div className="row g-2">
                {(gallery.length > 2 ? gallery.slice(-2) : gallery).map((item, idx) => (
                  <div className="col-6" key={idx}>
                    <div className="border rounded overflow-hidden" style={{ height: '100px' }}>
                      <img src={item.Image} alt={item.Description} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <small className="text-muted text-xs text-truncate d-block mt-1">{item.Description}</small>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}